import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { io, type Socket } from 'socket.io-client'
import { toast } from '@crosscode/gravity-components'
import { useAuth } from '@/app/AuthProvider'
import { fetchChatMessages, uploadChatFiles } from './api'
import type { ChatMessage, ChatOnlineUser } from './types'
import { mergeMessages, readLastReadId, saveLastReadId, sortMessagesAsc } from './utils'

interface ChatContextValue {
  open: boolean
  setOpen: (v: boolean) => void
  connected: boolean
  messages: ChatMessage[]
  onlineUsers: ChatOnlineUser[]
  typingUsers: string[]
  unread: number
  loading: boolean
  loadingMore: boolean
  hasMore: boolean
  sending: boolean
  loadMore: () => Promise<void>
  sendMessage: (text: string, files?: File[], replyToId?: number | null) => Promise<void>
  editMessage: (id: number, text: string) => void
  deleteMessage: (id: number) => void
  startTyping: () => void
  stopTyping: () => void
  markRead: () => void
}

const ChatContext = createContext<ChatContextValue | null>(null)

export function ChatProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth()
  const [open, setOpen] = useState(false)
  const [connected, setConnected] = useState(false)
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [onlineUsers, setOnlineUsers] = useState<ChatOnlineUser[]>([])
  const [typingMap, setTypingMap] = useState<Record<string, string>>({})
  const [unread, setUnread] = useState(0)
  const [loading, setLoading] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [hasMore, setHasMore] = useState(false)
  const [sending, setSending] = useState(false)

  const socketRef = useRef<Socket | null>(null)
  const openRef = useRef(open)
  const lastReadRef = useRef(readLastReadId())
  const typingTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  openRef.current = open

  const typingUsers = useMemo(
    () => Object.values(typingMap).filter((n) => n && n !== user?.name),
    [typingMap, user?.name],
  )

  const markRead = useCallback(() => {
    const last = messages[messages.length - 1]
    if (last) {
      lastReadRef.current = last.id
      saveLastReadId(last.id)
    }
    setUnread(0)
  }, [messages])

  const loadInitial = useCallback(async () => {
    setLoading(true)
    try {
      const page = await fetchChatMessages({ limit: 50 })
      const sorted = sortMessagesAsc(page.messages)
      setMessages(sorted)
      setHasMore(page.hasMore)
      const last = sorted[sorted.length - 1]
      if (last && last.id > lastReadRef.current && !openRef.current) {
        setUnread(sorted.filter((m) => m.id > lastReadRef.current).length)
      }
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Не удалось загрузить чат')
    } finally {
      setLoading(false)
    }
  }, [])

  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore || !messages.length) return
    const oldest = messages[0]
    setLoadingMore(true)
    try {
      const page = await fetchChatMessages({ beforeId: oldest.id, limit: 50 })
      setMessages((prev) => mergeMessages(prev, page.messages))
      setHasMore(page.hasMore)
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Не удалось подгрузить сообщения')
    } finally {
      setLoadingMore(false)
    }
  }, [hasMore, loadingMore, messages])

  useEffect(() => {
    if (!user?.id) {
      socketRef.current?.disconnect()
      socketRef.current = null
      setConnected(false)
      setMessages([])
      setOnlineUsers([])
      setUnread(0)
      return
    }

    void loadInitial()

    const socket = io({
      path: '/socket.io',
      withCredentials: true,
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 1000,
    })
    socketRef.current = socket

    socket.on('connect', () => setConnected(true))
    socket.on('disconnect', () => setConnected(false))
    socket.on('connect_error', () => setConnected(false))

    socket.on('onlineUsers', (users: ChatOnlineUser[]) => {
      setOnlineUsers(Array.isArray(users) ? users : [])
    })

    socket.on('newMessage', (msg: ChatMessage) => {
      setMessages((prev) => mergeMessages(prev, [msg]))
      if (openRef.current) {
        lastReadRef.current = Math.max(lastReadRef.current, msg.id)
        saveLastReadId(lastReadRef.current)
        setUnread(0)
      } else if (msg.user_id !== user.id) {
        setUnread((n) => n + 1)
      }
    })

    socket.on('messageEdited', (msg: ChatMessage) => {
      setMessages((prev) => mergeMessages(prev, [msg]))
    })

    socket.on('messageDeleted', (msg: ChatMessage | { id: number; name?: string }) => {
      setMessages((prev) =>
        prev.map((m) =>
          m.id === msg.id
            ? {
                ...m,
                ...('text' in msg ? msg : {}),
                deleted_at: 'deleted_at' in msg && msg.deleted_at ? msg.deleted_at : new Date().toISOString(),
                text: '',
                attachments: [],
              }
            : m,
        ),
      )
    })

    socket.on('userTyping', (data: { user?: string; userId?: number; typing?: boolean }) => {
      const key = data.userId != null ? String(data.userId) : data.user || ''
      if (!key) return
      setTypingMap((prev) => {
        const next = { ...prev }
        if (data.typing) next[key] = data.user || 'Кто-то'
        else delete next[key]
        return next
      })
    })

    socket.on('error', (err: { message?: string }) => {
      if (err?.message) toast.error(err.message)
    })

    return () => {
      socket.disconnect()
      socketRef.current = null
      setConnected(false)
    }
  }, [user?.id, loadInitial])

  useEffect(() => {
    if (open) markRead()
  }, [open, markRead])

  const sendMessage = useCallback(
    async (text: string, files: File[] = [], replyToId?: number | null) => {
      const trimmed = text.trim()
      if (!trimmed && !files.length) return
      const socket = socketRef.current
      if (!socket?.connected) {
        toast.error('Нет соединения с чатом')
        return
      }
      setSending(true)
      try {
        const attachments = files.length ? await uploadChatFiles(files) : []
        socket.emit('sendMessage', {
          text: trimmed,
          attachments,
          reply_to_id: replyToId || null,
        })
      } catch (e) {
        toast.error(e instanceof Error ? e.message : 'Ошибка отправки')
      } finally {
        setSending(false)
      }
    },
    [],
  )

  const editMessage = useCallback((id: number, text: string) => {
    const socket = socketRef.current
    if (!socket?.connected) {
      toast.error('Нет соединения с чатом')
      return
    }
    socket.emit('editMessage', { id, text: text.trim() })
  }, [])

  const deleteMessage = useCallback((id: number) => {
    const socket = socketRef.current
    if (!socket?.connected) {
      toast.error('Нет соединения с чатом')
      return
    }
    socket.emit('deleteMessage', { id })
  }, [])

  const startTyping = useCallback(() => {
    socketRef.current?.emit('startTyping')
    if (typingTimer.current) clearTimeout(typingTimer.current)
    typingTimer.current = setTimeout(() => {
      socketRef.current?.emit('stopTyping')
    }, 2000)
  }, [])

  const stopTyping = useCallback(() => {
    if (typingTimer.current) clearTimeout(typingTimer.current)
    socketRef.current?.emit('stopTyping')
  }, [])

  const value = useMemo(
    () => ({
      open,
      setOpen,
      connected,
      messages,
      onlineUsers,
      typingUsers,
      unread,
      loading,
      loadingMore,
      hasMore,
      sending,
      loadMore,
      sendMessage,
      editMessage,
      deleteMessage,
      startTyping,
      stopTyping,
      markRead,
    }),
    [
      open,
      connected,
      messages,
      onlineUsers,
      typingUsers,
      unread,
      loading,
      loadingMore,
      hasMore,
      sending,
      loadMore,
      sendMessage,
      editMessage,
      deleteMessage,
      startTyping,
      stopTyping,
      markRead,
    ],
  )

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>
}

export function useChat() {
  const ctx = useContext(ChatContext)
  if (!ctx) throw new Error('useChat must be used within ChatProvider')
  return ctx
}
